import {Pagination} from '@store/common/pagination.model';
import I18n from '@utils/i18n.utils';
import {OrganisationLight, OrganisationUnitLight} from '@store/common/organisation.model';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';

export enum EmailPreferences {
  ALL = 'ALL',
  NONE = 'NONE',
}

export namespace EmailPreferences {
  export const selectItems = () => [
    {label: I18n.t('global.emailPreferences.all'), value: EmailPreferences.ALL},
    {label: I18n.t('global.emailPreferences.none'), value: EmailPreferences.NONE},
  ];
}

export enum Roles {
  WASTE = 'WASTE_DIRECTOR',
  KEY = 'KEY_ACCOUNT_MANAGER',
  EXTERNAL = 'EXTERNAL_SITE_OPERATOR',
  ADMIN = 'ADMIN',
  HAULING = 'HAULING_COMPANY_OWNER',
  INTERNAL = 'INTERNAL_SITE_OPERATOR',
  WORKSITE = 'WORKSITE',
}

export namespace Roles {
  export const selectItems = () => [
    {label: I18n.t('global.roles.waste'), value: Roles.WASTE},
    {label: I18n.t('global.roles.key'), value: Roles.KEY},
    {label: I18n.t('global.roles.external'), value: Roles.EXTERNAL},
    {label: I18n.t('global.roles.admin'), value: Roles.ADMIN},
    {label: I18n.t('global.roles.hauling'), value: Roles.HAULING},
    {label: I18n.t('global.roles.internal'), value: Roles.INTERNAL},
    {label: I18n.t('global.roles.worksite'), value: Roles.WORKSITE}
  ];
}

export enum UserType {
  CLIENT = 'CLIENT',
  SUPPLIER = 'SUPPLIER',
  ADMIN = 'ADMIN',
}

export namespace UserType {
  export const selectItems = () => [
    {label: I18n.t('global.userType.client'), value: UserType.CLIENT},
    {label: I18n.t('global.userType.supplier'), value: UserType.SUPPLIER},
    {label: I18n.t('global.userType.admin'), value: UserType.ADMIN},
  ];
}

export enum UserPreferencesEnum {
  emailOnExecutionToBeTreated = 'emailOnExecutionToBeTreated',
  emailOnExecutionAccepted = 'emailOnExecutionAccepted',
  emailOnExecutionDenied = 'emailOnExecutionDenied',
  emailOnExecutionEnded = 'emailOnExecutionEnded',
  emailOnExecutionNewComment = 'emailOnExecutionNewComment',
  emailOnExecutionNewIssue = 'emailOnExecutionNewIssue',
  emailOnExecutionIssueClosed = 'emailOnExecutionIssueClosed',
  emailOnExecutionUpdated = 'emailOnExecutionUpdated',
  emailOnExecutionIssueUpdated = 'emailOnExecutionIssueUpdated',
  emailOnExecutionCanceled = 'emailOnExecutionCanceled',
}

export namespace UserPreferences {
  export const selectItems = () => [
    {
      label: I18n.t('global.userPreferences.emailOnExecutionToBeTreated'),
      value: UserPreferencesEnum.emailOnExecutionToBeTreated
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionAccepted'),
      value: UserPreferencesEnum.emailOnExecutionAccepted
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionDenied'),
      value: UserPreferencesEnum.emailOnExecutionDenied
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionEnded'),
      value: UserPreferencesEnum.emailOnExecutionEnded
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionNewComment'),
      value: UserPreferencesEnum.emailOnExecutionNewComment
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionNewIssue'),
      value: UserPreferencesEnum.emailOnExecutionNewIssue
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionIssueClosed'),
      value: UserPreferencesEnum.emailOnExecutionIssueClosed
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionUpdated'),
      value: UserPreferencesEnum.emailOnExecutionUpdated
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionIssueUpdated'),
      value: UserPreferencesEnum.emailOnExecutionIssueUpdated
    },
    {
      label: I18n.t('global.userPreferences.emailOnExecutionCanceled'),
      value: UserPreferencesEnum.emailOnExecutionCanceled
    }
  ];
}

export interface UserPreferences {
  emailOnExecutionToBeTreated: boolean;
  emailOnExecutionAccepted: boolean;
  emailOnExecutionDenied: boolean;
  emailOnExecutionEnded: boolean;
  emailOnExecutionNewComment: boolean;
  emailOnExecutionNewIssue: boolean;
  emailOnExecutionIssueClosed: boolean;
  emailOnExecutionUpdated: boolean;
  emailOnExecutionIssueUpdated: boolean;
  emailOnExecutionCanceled: boolean;
}


export interface UserUnit {
  id?: number;
  name?: string;
}

export interface UserOrganisationAndUnits {
  id?: number;
  name?: string;
  units?: UserUnit[];
}

export interface ManageUser {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  language?: string;
  userPreferences?: UserPreferencesEnum[];
  emailPreferences?: EmailPreferences;
  roles?: Roles[];
  organisationAndUnits?: UserOrganisationAndUnits[];
  organisations?: OrganisationLight[];
  units?: OrganisationUnitLight[];
  hasStatsAccess?: boolean;
}

export interface UserLight {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UsersFilters {
  search: string;
  tableFilters: TableFilter[];
}

export namespace UsersFilters {
  export const initTableFilters = () => TableFilterType.initTableFilters([
    TableFilterType.CLIENT_ORGANISATION,
    TableFilterType.CLIENT_ORGANISATION_UNIT,
    TableFilterType.SUPPLIER_ORGANISATION,
    TableFilterType.SUPPLIER_ORGANISATION_UNIT,
  ]);
}

export interface UsersPagination extends Pagination {
  data: User[];
}

export namespace User {
  export const toManageUser = (user: User): ManageUser => ({
    ...user,
    userPreferences: Object.keys(user.userPreferences || []).reduce((acc: UserPreferencesEnum[], key) => {
      if (user.userPreferences?.[key] === true) {
        acc.push(key as UserPreferencesEnum);
      }
      return acc;
    }, [])
  });
}

export interface User {
  id: number;
  firstName?: string;
  lastName?: string;
  enterprise?: string;
  email?: string;
  language?: string;
  userPreferences?: UserPreferences;
  type?: string;
  roles: Roles[];
  createdDate: Date;
  clients: OrganisationLight[];
  suppliers: OrganisationLight[];
  organisationAndUnits?: UserOrganisationAndUnits[];
  organisations?: OrganisationLight[];
  units?: OrganisationUnitLight[];
  lastActivityAt?: Date;
}
